import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'components/Modal'
import { ModalActionFooter } from 'components/Modal/ModalFooter'
import Input from 'components/Form/Input'
import InputTitle from 'components/Form/InputTitle'
import RadioButton from 'components/Form/RadioButton'
import FormRow from 'components/Form/FormRow'
import FormColumn from 'components/Form/FormColumn'
import { InputMask } from 'utils/inputMask'
import { AppContext } from 'contexts/AppContext'
import { CURRENCY_PLN } from 'constant'
import { Disposition, DispositionCorrection } from 'types/disposition'
import service from '../service'

/**
 * The backend reads the direction from the sign of `percentChange`, but a masked decimal input
 * cannot take a minus sign (`InputMask.FLOAT` sets `allowMinus: false`) and a typed one would be
 * easy to miss anyway, so the direction is picked explicitly and the amount stays unsigned.
 */
enum CorrectionDirection {
  DECREASE = 'decrease',
  INCREASE = 'increase',
}

/** The correction is entered either as a percent or as the exact price after it. */
enum CorrectionInputMode {
  PERCENT = 'byPercent',
  PRICE = 'byPrice',
}

/** Every expanded disposition row renders its own modal, so the radio groups have to stay unique. */
const radioInputId = (group: string, dispositionId: Disposition['id'], value: string): string =>
  `${group}-${dispositionId}-${value}`

const parseAmount = (value: string): number => parseFloat(value.replace(',', '.'))

interface CorrectionModalProps {
  disposition: Disposition
  correction?: DispositionCorrection | null
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  onSaved: () => void
}

const CorrectionModal: React.FC<CorrectionModalProps> = ({
  disposition,
  correction,
  isOpen,
  setIsOpen,
  onSaved,
}) => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const [inputMode, setInputMode] = useState<CorrectionInputMode>(CorrectionInputMode.PERCENT)
  const [direction, setDirection] = useState<CorrectionDirection>(CorrectionDirection.DECREASE)
  const [percentChange, setPercentChange] = useState<string>('')
  const [correctedPriceInput, setCorrectedPriceInput] = useState<string>('')
  const [correctionReason, setCorrectionReason] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setInputMode(CorrectionInputMode.PERCENT)
    setDirection(
      correction && correction.percentChange > 0
        ? CorrectionDirection.INCREASE
        : CorrectionDirection.DECREASE
    )
    setPercentChange(correction ? Math.abs(correction.percentChange).toString() : '')
    setCorrectedPriceInput(correction ? correction.correctedPrice.toString() : '')
    setCorrectionReason(correction?.correctionReason ?? '')
  }, [isOpen, correction])

  const isPriceMode = inputMode === CorrectionInputMode.PRICE
  const basePrice = service.basePrice(disposition, correction)
  const isCorrected = basePrice !== disposition.price

  const parsedAmount = parseAmount(percentChange)
  const isPercentChangeValid = !isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount <= 100
  const signedPercentChange =
    direction === CorrectionDirection.INCREASE ? parsedAmount : -parsedAmount

  // Mirrors the backend bounds of the percent change (-100..100, other than 0).
  const parsedPrice = parseAmount(correctedPriceInput)
  const isCorrectedPriceValid =
    !isNaN(parsedPrice) &&
    parsedPrice >= 0 &&
    parsedPrice !== basePrice &&
    parsedPrice <= 2 * basePrice

  const isAmountValid = isPriceMode ? isCorrectedPriceValid : isPercentChangeValid
  const isValid = isAmountValid && correctionReason.trim().length > 0

  // Whichever value was entered is shown as is, the other one is derived the way the backend does.
  const [correctedPrice, resultingPercentChange] = !isAmountValid
    ? [null, null]
    : isPriceMode
    ? [parsedPrice, service.percentChangeFor(basePrice, parsedPrice)]
    : [service.correctedPrice(basePrice, signedPercentChange), signedPercentChange]

  const handleSave = (): Promise<void> =>
    service
      .upsertCorrection(
        {
          dispositionId: disposition.id,
          correctionId: correction?.id ?? null,
          ...(isPriceMode
            ? { correctedPrice: parsedPrice }
            : { percentChange: signedPercentChange }),
          correctionReason: correctionReason.trim(),
        },
        addNotification,
        t
      )
      .then(success => {
        if (success) {
          onSaved()
        }
      })

  return (
    <Modal
      isOpen={isOpen}
      isLoading={isLoading}
      onClose={() => setIsOpen(false)}
      title={t(
        correction
          ? 'agencies.dispositions.corrections.editTitle'
          : 'agencies.dispositions.corrections.addTitle'
      )}
      footer={
        <ModalActionFooter
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setIsOpen={setIsOpen}
          onAction={handleSave}
          actionText={t('common.save')}
          actionButtonDisabled={!isValid}
        />
      }
    >
      <div className='CorrectionModal'>
        <FormRow>
          <FormColumn>
            <InputTitle title={t('agencies.dispositions.corrections.inputMode')} />
            <div
              className='CorrectionModal__direction'
              data-cy='correction-input-mode'
            >
              {[CorrectionInputMode.PERCENT, CorrectionInputMode.PRICE].map(item => (
                <RadioButton
                  key={item}
                  id={radioInputId('correctionInputMode', disposition.id, item)}
                  groupId={radioInputId('correctionInputMode', disposition.id, 'group')}
                  value={radioInputId('correctionInputMode', disposition.id, inputMode)}
                  onChange={() => setInputMode(item)}
                >
                  {t(`agencies.dispositions.corrections.${item}`)}
                </RadioButton>
              ))}
            </div>
          </FormColumn>
        </FormRow>

        {isPriceMode ? (
          <FormRow>
            <FormColumn>
              <Input
                dataCy='correction-corrected-price-input'
                id='correctedPrice'
                title={t('agencies.dispositions.corrections.correctedPrice')}
                placeholder={t('agencies.dispositions.corrections.correctedPrice')}
                value={correctedPriceInput}
                maskType={InputMask.FLOAT}
                suffix={CURRENCY_PLN}
                onChange={(value: string) => setCorrectedPriceInput(value)}
                errors={
                  correctedPriceInput && !isCorrectedPriceValid
                    ? [t('agencies.dispositions.corrections.correctedPriceError')]
                    : undefined
                }
              />
            </FormColumn>
          </FormRow>
        ) : (
          <>
            <FormRow>
              <FormColumn>
                <InputTitle title={t('agencies.dispositions.corrections.direction')} />
                <div
                  className='CorrectionModal__direction'
                  data-cy='correction-direction'
                >
                  {[CorrectionDirection.DECREASE, CorrectionDirection.INCREASE].map(item => (
                    <RadioButton
                      key={item}
                      id={radioInputId('correctionDirection', disposition.id, item)}
                      groupId={radioInputId('correctionDirection', disposition.id, 'group')}
                      value={radioInputId('correctionDirection', disposition.id, direction)}
                      onChange={() => setDirection(item)}
                    >
                      {t(`agencies.dispositions.corrections.${item}`)}
                    </RadioButton>
                  ))}
                </div>
              </FormColumn>
            </FormRow>

            <FormRow>
              <FormColumn>
                <Input
                  dataCy='correction-percent-change'
                  id='percentChange'
                  title={t('agencies.dispositions.corrections.percentAmount')}
                  placeholder={t('agencies.dispositions.corrections.percentAmount')}
                  value={percentChange}
                  maskType={InputMask.PRECISE_FLOAT}
                  suffix='%'
                  onChange={(value: string) => setPercentChange(value)}
                  errors={
                    percentChange && !isPercentChangeValid
                      ? [t('agencies.dispositions.corrections.percentChangeError')]
                      : undefined
                  }
                />
              </FormColumn>
            </FormRow>
          </>
        )}

        <FormRow>
          <FormColumn>
            <div className='CorrectionModal__reason'>
              <span className='CorrectionModal__reason-title'>
                {t('agencies.dispositions.corrections.reason')}
              </span>
              <textarea
                data-cy='correction-reason'
                value={correctionReason}
                placeholder={t('agencies.dispositions.corrections.reasonPlaceholder')}
                onChange={event => setCorrectionReason(event.target.value)}
              />
            </div>
          </FormColumn>
        </FormRow>

        <div className='CorrectionModal__summary'>
          {isCorrected && (
            <div className='CorrectionModal__summary-item'>
              <span>{t('agencies.dispositions.corrections.dispositionPrice')}</span>
              <strong>{service.formatPrice(disposition.price)}</strong>
            </div>
          )}
          <div className='CorrectionModal__summary-item'>
            <span>{t('agencies.dispositions.corrections.originalPrice')}</span>
            <strong data-cy='correction-base-price'>{service.formatPrice(basePrice)}</strong>
          </div>
          <div className='CorrectionModal__summary-item'>
            <span>{t('agencies.dispositions.corrections.percentChange')}</span>
            <strong data-cy='correction-resulting-percent-change'>
              {resultingPercentChange === null
                ? '—'
                : service.formatPercent(resultingPercentChange)}
            </strong>
          </div>
          <div className='CorrectionModal__summary-item'>
            <span>{t('agencies.dispositions.corrections.correctedPrice')}</span>
            <strong data-cy='correction-corrected-price'>
              {correctedPrice === null ? '—' : service.formatPrice(correctedPrice)}
            </strong>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default CorrectionModal
